import React from "react";
import { toastService } from "@/shared/toast";
import type { RecordItem } from "@/features/home/viewModal/homePageViewModal";
import styles from "./CommunityRecordDetailModal.module.css";

interface CommunityRecordDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  record: RecordItem | null;
  effectiveAddress?: string;
}

export const CommunityRecordDetailModal: React.FC<CommunityRecordDetailModalProps> = ({
  isOpen,
  onClose,
  record,
  effectiveAddress,
}) => {
  if (!isOpen || !record) return null;

  const isOffender = record.type === "offender";
  const offenderData = record.type === "offender" ? record.data : null;
  const crimeData = record.type === "crime" ? record.data : null;

  // Extract display values
  const name = isOffender
    ? offenderData?.sex_offender_name ||
      [
        offenderData?.sex_offender_first_name,
        offenderData?.sex_offender_middle_name,
        offenderData?.sex_offender_last_name,
      ]
        .filter(Boolean)
        .join(" ") ||
      record.name ||
      "Registered Offender"
    : crimeData?.incident_offense ||
      crimeData?.incident_offense_description ||
      record.name ||
      "Crime Incident";

  const imageUrl = isOffender
    ? offenderData?.sex_offender_image_url || record.imageUrl
    : record.imageUrl;

  const address = isOffender
    ? offenderData?.sex_offender_address_line1 ||
      offenderData?.sex_offender_location ||
      effectiveAddress ||
      "Address not available"
    : crimeData?.incident_address || effectiveAddress || "Address not available";

  const charges = isOffender
    ? offenderData?.sex_offender_charges || "Charges not available"
    : crimeData?.incident_offense_detail_description ||
      crimeData?.incident_offense_crime_against ||
      crimeData?.incident_offense_description ||
      crimeData?.incident_offense ||
      "Incident details not available";

  const isPredator =
    Boolean(offenderData?.sex_offender_is_predator === "true") ||
    Boolean(String(offenderData?.sex_offender_is_predator) === "1");

  const isAbsconder =
    Boolean(offenderData?.sex_offender_is_absconder === "true") ||
    Boolean(String(offenderData?.sex_offender_is_absconder) === "1");

  // Map link coordinates or address query
  const lat = isOffender ? offenderData?.sex_offender_lat : crimeData?.incident_latitude;
  const lon = isOffender ? offenderData?.sex_offender_lon : crimeData?.incident_longitude;
  const locationQuery =
    lat && lon
      ? `${lat},${lon}`
      : address !== "Address not available"
        ? address
        : effectiveAddress || "";

  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationQuery)}`;

  const handleShare = async () => {
    const title = isOffender ? `Offender Details: ${name}` : `Crime Incident: ${name}`;
    const text = `${name}\nAddress: ${address}\nCharges: ${charges}`;
    const url = window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
      } catch {
        // User cancelled or share failed, silent fallback
      }
    } else {
      try {
        await navigator.clipboard.writeText(`${title}\n${text}\nLink: ${url}`);
        toastService.show("Details copied to clipboard!", "success");
      } catch {
        toastService.show("Could not copy to clipboard", "error");
      }
    }
  };

  return (
    <div
      className={styles.modalOverlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="community-modal-title"
    >
      <div className={styles.modalCard}>
        {/* Modal Header */}
        <div className={styles.modalHeader}>
          <h3 id="community-modal-title" className={styles.modalTitle}>
            {isOffender ? "Offender Details" : "Crime Details"}
          </h3>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        {/* Modal Body */}
        <div className={styles.modalBody}>
          {/* Centered Image with Amber/Gold Border */}
          <div className={styles.photoContainer}>
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={name}
                className={styles.offenderPhoto}
                referrerPolicy="no-referrer"
              />
            ) : isOffender ? (
              <div className={styles.photoPlaceholder} role="img" aria-label="Offender silhouette">
                👤
              </div>
            ) : (
              <div className={styles.crimePlaceholder} role="img" aria-label="Crime incident">
                🚨
              </div>
            )}
          </div>

          {/* Offender / Incident Name */}
          <div className={styles.offenderName}>{name}</div>

          {/* Details Section */}
          <div className={styles.detailsList}>
            {/* Location Link */}
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Location:</span>
              <a
                href={googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.locationLink}
              >
                {isOffender
                  ? "Click here to see where they live."
                  : "Click here to see where it happened."}
              </a>
            </div>

            {/* Address */}
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Address:</span>
              <span className={styles.detailValue}>{address}</span>
            </div>

            {/* Charges */}
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>
                {isOffender ? "Charges:" : "Charges / Offense:"}
              </span>
              <span className={styles.detailValue}>{charges}</span>
            </div>

            {/* Optional Flags */}
            {(isPredator || isAbsconder) && (
              <div className={styles.badgeRow}>
                {isPredator && <span className={styles.predatorBadge}>⚠️ Sexual Predator</span>}
                {isAbsconder && <span className={styles.absconderBadge}>🚨 Absconder</span>}
              </div>
            )}
          </div>

          {/* Share Button */}
          <button type="button" className={styles.shareBtn} onClick={handleShare}>
            <svg
              className={styles.shareIcon}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
            <span>Share</span>
          </button>
        </div>
      </div>
    </div>
  );
};
